import React from "react";
import { Link } from "react-router-dom";
import type { AdminEvent } from "../api.js";
import { JobDebugPanel } from "./AdminJobDebug.js";
import { groupByDay, time, toneClass, usd, type Tone } from "./format.js";

/** What a line is, in the words the mockup uses. An attempt says which kind it was. */
function kindLabel(event: AdminEvent): string {
  switch (event.type) {
    case "attempt":
      return event.detail.kind === "ai_lookup" ? "AI lookup" : "Site read";
    case "project":
      return "Project";
    case "job":
      return "Job";
    case "credit":
      return "Credit";
  }
}

/** "not_found" → "Not found". */
function outcomeLabel(outcome: string): string {
  const words = outcome.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function eventTone(event: AdminEvent): Tone {
  if (event.failed) return "bad";
  // A lookup that found nothing: worth seeing, not a failure.
  if (event.outcome === "not_found") return "warn";
  if (event.outcome === "completed" || event.outcome === "found" || event.outcome === "ok") return "ok";
  return "muted";
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : typeof value === "number" ? String(value) : null;
const count = (value: unknown): string | null => (typeof value === "number" ? value.toLocaleString("en-US") : null);

const PAYLOAD_LABELS: [field: string, label: string][] = [
  ["website", "website"],
  ["pageSource", "page from"],
  ["templateId", "template"],
  ["seconds", "seconds"],
  ["name", "name"],
];

/** The allowlisted payload fields a project or job carries, as one "Asked:" line. */
function asked(detail: Record<string, unknown>): string | null {
  const parts = PAYLOAD_LABELS.flatMap(([field, label]) => {
    const value = text(detail[field]);
    return value ? [`${label} ${value}`] : [];
  });
  return parts.length ? `Asked: ${parts.join(" · ")}` : null;
}

/**
 * Whether a source's address is safe to link. The sources come from a model
 * reading the web, so anything but http(s) (javascript:, data:, …) is shown as
 * text rather than put in an href.
 */
function isWebUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="rounded-[4px] bg-bay-800 px-1.5 py-px font-mono text-[11.5px] text-ink-200">{children}</code>
);

const Meta: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-[11.5px] tabular-nums text-ink-400">{children}</div>
);

const ProjectLink: React.FC<{ projectId: string | null }> = ({ projectId }) =>
  projectId ? (
    <Meta>
      <Link to={`/projects/${projectId}`} className="text-scope-400 hover:underline">
        Open project (read only) →
      </Link>
    </Meta>
  ) : null;

/** The open line's detail, by what kind of event it is. */
const Detail: React.FC<{ event: AdminEvent }> = ({ event }) => {
  const { detail } = event;
  const by = event.who ? <span>by {event.who}</span> : null;

  if (event.type === "attempt") {
    const summary = text(detail.summary);
    const sources = Array.isArray(detail.sources)
      ? (detail.sources as unknown[]).flatMap((source) => {
          if (!source || typeof source !== "object") return [];
          const { title, url } = source as { title?: unknown; url?: unknown };
          return typeof url === "string" ? [{ title: text(title) ?? url, url }] : [];
        })
      : [];
    const inTokens = count(detail.inputTokens);
    const outTokens = count(detail.outputTokens);
    if (summary) {
      return (
        <>
          <p className="text-[12.5px] leading-relaxed text-ink-100">{summary}</p>
          {sources.length ? (
            <ul className="flex list-disc flex-col gap-0.5 pl-[18px] text-[12px] text-ink-300">
              {sources.map((source, index) => (
                <li key={`${index}:${source.url}`} className="break-words">
                  {isWebUrl(source.url) ? (
                    <a href={source.url} target="_blank" rel="noreferrer" className="text-scope-400 hover:underline">
                      {source.title}
                    </a>
                  ) : (
                    <span className="text-ink-200">{source.title}</span>
                  )}{" "}
                  <span className="text-[11px] text-ink-400">{source.url}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <Meta>
            {text(detail.model) ? <span>{text(detail.model)}</span> : null}
            {inTokens && outTokens ? (
              <span>
                {inTokens} in · {outTokens} out tokens
              </span>
            ) : null}
            {event.costUsd !== null ? <span>{usd(event.costUsd)}</span> : null}
            {by}
          </Meta>
        </>
      );
    }
    const duration = typeof detail.durationMs === "number" ? `${(detail.durationMs / 1000).toFixed(1)} s` : null;
    return (
      <Meta>
        {text(detail.status) ? (
          <span>
            Status <Code>{text(detail.status)}</Code>
          </span>
        ) : null}
        {duration ? <span>{duration}</span> : null}
        {text(detail.model) ? <span>{text(detail.model)}</span> : null}
        {event.costUsd !== null ? <span>{usd(event.costUsd)}</span> : null}
        {by}
      </Meta>
    );
  }

  if (event.type === "job") {
    const line = asked(detail);
    return (
      <>
        {text(detail.error) ? <p className="text-[12.5px] leading-relaxed text-ink-100">{text(detail.error)}</p> : null}
        <Meta>
          {text(detail.step) ? (
            <span>
              Step <Code>{text(detail.step)}</Code>
            </span>
          ) : null}
          {text(detail.errorCode) ? <Code>{text(detail.errorCode)}</Code> : null}
          {typeof detail.attempts === "number" && typeof detail.maxAttempts === "number" ? (
            <span>
              attempt {detail.attempts} of {detail.maxAttempts}
            </span>
          ) : null}
        </Meta>
        {line ? (
          <Meta>
            <span>{line}</span>
          </Meta>
        ) : null}
        <Meta>
          {event.costUsd !== null ? <span>Our cost so far {usd(event.costUsd)}</span> : null}
          {by}
        </Meta>
        <ProjectLink projectId={event.projectId} />
        {/* Failed jobs only: the recording and the full record are for debugging a failure. */}
        {event.failed ? <JobDebugPanel jobId={event.id} /> : null}
      </>
    );
  }

  if (event.type === "project") {
    const line = asked(detail);
    return (
      <>
        <Meta>
          {text(detail.kind) ? <span>{detail.kind === "launch" ? "Launch film" : "Product demo"}</span> : null}
          {line ? <span>{line}</span> : null}
          {by}
        </Meta>
        <ProjectLink projectId={event.projectId} />
      </>
    );
  }

  // Credit.
  return (
    <Meta>
      {text(detail.note) ? <span>{text(detail.note)}</span> : null}
      {text(detail.projectName) ? <span>For {text(detail.projectName)}</span> : null}
      {detail.usedFreeVideo === true ? <span>Used the free video</span> : null}
      {detail.freeForever === true ? <span>Free forever</span> : null}
      {by}
    </Meta>
  );
};

/**
 * One team's events, newest first, grouped by day.
 *
 * Pure: the page owns which line is open, so a refetch for another filter can
 * close it, and a test can render a line open.
 */
export const AdminTimeline: React.FC<{
  events: AdminEvent[];
  openId: string | null;
  onOpen: (id: string | null) => void;
  now: Date;
}> = ({ events, openId, onOpen, now }) => {
  if (events.length === 0) {
    return (
      <div className="rounded-[10px] border border-[var(--edge)] bg-bay-850 px-4 py-10 text-center text-[13px] text-ink-300">
        Nothing here yet.
      </div>
    );
  }
  return (
    <div className="flex flex-col overflow-hidden rounded-[10px] border border-[var(--edge)] bg-bay-850">
      {groupByDay(events, now).map((group) => (
        <section key={group.label}>
          <h3 className="border-b border-[var(--edge)] bg-bay-900 px-3.5 pb-1.5 pt-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-400">
            {group.label}
            {/* Days and times are both UTC; said once per day rather than on every row. */}
            <span className="ml-1.5 font-normal normal-case tracking-normal">(UTC)</span>
          </h3>
          <ul>
            {group.events.map((event) => {
              const open = event.id === openId;
              const detailId = `admin-event-${event.id}`;
              return (
                <li
                  key={event.id}
                  className={`border-b border-[var(--edge)] last:border-b-0 ${open ? "bg-scope-400/[0.07]" : ""}`}
                >
                  {/* The row is the button; the detail sits below it, outside the
                      button, so its links are links and not nested controls. */}
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={open ? detailId : undefined}
                    onClick={() => onOpen(open ? null : event.id)}
                    className="grid w-full grid-cols-[44px_minmax(0,1fr)_auto] items-start gap-2.5 px-3.5 py-[11px] text-left text-[13px] transition-colors hover:bg-bay-900 focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)] sm:grid-cols-[52px_92px_minmax(0,1fr)_auto]"
                  >
                    <time dateTime={event.at} className="pt-px text-[12px] tabular-nums text-ink-400">
                      {time(event.at)}
                    </time>
                    <span className="hidden pt-0.5 text-[11px] font-semibold uppercase tracking-[0.03em] text-ink-300 sm:block">
                      {kindLabel(event)}
                    </span>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <b className="break-words font-semibold text-ink-100">{event.title}</b>
                      {event.who ? <small className="truncate text-[12px] text-ink-300">{event.who}</small> : null}
                    </span>
                    <span className="flex flex-col items-end gap-0.5 text-right text-[12.5px] tabular-nums">
                      {event.amountUsd !== null ? (
                        <span className={event.amountUsd < 0 ? "text-[#b91c1c]" : "text-ink-100"}>
                          {event.amountUsd > 0 ? "+" : ""}
                          {usd(event.amountUsd)}
                        </span>
                      ) : event.outcome ? (
                        <span
                          className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${toneClass[eventTone(event)]}`}
                        >
                          {outcomeLabel(event.outcome)}
                        </span>
                      ) : null}
                      {event.costUsd !== null ? (
                        <small className="text-[11px] text-ink-400">cost {usd(event.costUsd)}</small>
                      ) : null}
                    </span>
                  </button>
                  {open ? (
                    <div
                      id={detailId}
                      className="mx-3.5 mb-3 flex flex-col gap-2 rounded-[8px] border border-[var(--edge)] bg-bay-850 px-3 py-2.5 sm:ml-[168px]"
                    >
                      <Detail event={event} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
